import App from '@/App.vue'
import BackLinkComponent from '@/components/BackLinkComponent'
import connectHttpBase from '@/lib/connect-client/connect-http-base'
import * as filters from '@/lib/filters'
import storage from '@/lib/storage'
import vuetify from '@/plugins/vuetify'
import router from '@/router'
import store from '@/store'
import '@mdi/font/css/materialdesignicons.css'
import 'eventsource/lib/eventsource-polyfill'
import 'roboto-fontface/css/roboto/roboto-fontface.css'
import Vue from 'vue'
import VueGtag from 'vue-gtag'
import * as VueGoogleMaps from 'vue2-google-maps'

import WebFont from 'webfontloader'
import ApplicationConfig from './lib/application-config'
import * as Constants from './lib/constants'
import UserInfo from '@/lib/user-info'
import connectAuthClient from '@/lib/connect-client/connect-auth-client'

Vue.config.productionTip = false

WebFont.load({ google: { families: ['Montserrat:300', 'Montserrat:500'] } })

// Google Analytics4(gtag.js)
Vue.use(
  VueGtag,
  {
    config: { id: ApplicationConfig.getGtagTrackingId() },
  },
  router
)

// Google Map Api
Vue.use(VueGoogleMaps, {
  load: {
    key: Constants.GOOGLE_MAP_API_KEY,
    // 圃場作成の描画を DrawingManager 非依存 (クリック描画) に置き換えたため
    // drawing ライブラリとバージョン固定 (3.64) を解除 (2026-08-02)
    v: 'weekly',
    map_ids: Constants.GOOGLE_MAP_VECTOR_MAP_ID,
  },
  router,
})

// 戻り導線 (「＜ 遷移先名」)。全画面共通のデザインルールなのでグローバル登録する
Vue.component('BackLinkComponent', BackLinkComponent)

// Google Map Ground Overlay
Vue.component(
  'gmap-ground-overlay',
  VueGoogleMaps.MapElementFactory({
    mappedProps: {
      opacity: {},
    },
    props: {
      source: { type: String },
      bounds: { type: Object },
    },
    events: ['click', 'dblclick'],
    name: 'gMapGroundOverlay',
    ctr: () => window.google.maps.GroundOverlay,
    ctrArgs: (options, { source, bounds }) => [source, bounds, options],
  })
)

// Router Authentication Hook
router.beforeEach((to, from, next) => {
  if (canPassThrough(to, from)) {
    next()
  } else {
    // requireAuthがtrueの場合でかつAccessTokenがExpireしている場合
    if ((to.matched.some(record => record.meta.requireAuth) && !connectHttpBase.isAuthenticated()) || to.path === '/') {
      // もしstorageのuserInfoが壊れていたら、ログイン画面に飛ばす
      const userInfo = storage.getUserInfo()
      if (userInfo === undefined || userInfo === null) {
        next({ name: 'Login', query: { redirect: to.fullPath } })
      }

      let refreshToken = storage.getRefreshToken()
      if (storage.isLoginRemmberMe() && refreshToken) {
        // ログイン情報を保持し、RefreshTokenが有効な場合
        connectAuthClient
          .refreshAccessToken()
          .then(response => store.dispatch('authRefreshTokenSuccess', response.data))
          .then(() => {
            const purchaseInfo = storage.getPurchaseInfo()
            if (UserInfo.hasFreeFunctionality() || purchaseInfo.isGoogleCanceled()) {
              if (to.path === '/') {
                // トップページはニュースフィード (無料会員も閲覧可)
                next({ name: 'Feed' })
              } else if (isFreeAccessPath(to)) {
                next()
              } else {
                next({ name: 'FreeDashBoard' })
              }
            } else {
              if (to.path === '/') {
                // トップページはニュースフィード
                next({ name: 'Feed' })
              } else if (to.name === 'TaskDetailDirect' || to.name === 'TaskDetail') {
                // 作業詳細はURLのkeyから直接復元できる(リロード対応)
                const params = to.params
                const opts = {
                  taskDetailShowAction: Constants.SHOW_TASK_DETAIL_FROM_DIRECT,
                  key: params.key,
                }
                store.dispatch('getAllFields', opts)
              } else if (isDirectAccessPath(to)) {
                next()
              } else {
                next(fallbackRoute(to))
              }
            }
          })
          .catch(error => {
            console.error(error)
            next({ name: 'Login', query: { redirect: to.fullPath } })
          })
      } else {
        // ログイン情報を保持していない、もしくはRefreshTokenが有効ではない場合
        next({ name: 'Login', query: { redirect: to.fullPath } })
      }
    } else {
      // その他
      if (UserInfo.hasFreeFunctionality() && !isFreeAccessPath(to)) {
        next({ name: 'FreeDashBoard' })
      } else {
        next()
      }
    }
  }

  function canPassThrough(to, from) {
    return to.name === 'Login'
  }

  function isFreeAccessPath(to) {
    return (
      to.name === 'FreeDashBoard' ||
      to.name === 'UserSettings' ||
      to.name === 'UserPassword' ||
      to.name === 'UserIcon' ||
      to.name === 'UserPasswordSuccess' ||
      to.name === 'ApplicationConsole' ||
      to.name === 'ShopifySSO' ||
      to.name === 'Feed'
    )
  }

  function isDirectAccessPath(to) {
    // リロード時に現在の画面を復元できる画面(自前でデータ取得する、またはトークン復元時の初期化で足りる)
    return (
      to.name === 'BillingBilling' ||
      to.name === 'UserSettings' ||
      to.name === 'CasterInfo' ||
      to.name === 'Feed' ||
      to.name === 'MarketInsights' ||
      to.name === 'FieldListAsCard' ||
      to.name === 'FieldListAsList' ||
      to.name === 'FieldAdd' ||
      to.name === 'FieldCreate' ||
      to.name === 'FieldImport' ||
      to.name === 'TaskListAsList' ||
      to.name === 'TaskListAsCalendar' ||
      to.name === 'RefLineListAsList' ||
      to.name === 'PlantingListAsList' ||
      to.name === 'SolidMoistureListAsList' ||
      to.name === 'AccumulatedTemperatureListAsList' ||
      to.name === 'NowNow' ||
      to.name === 'FeedConnections' ||
      to.name === 'PoiRecords' ||
      to.name === 'WorkRecords' ||
      to.name === 'WorkEditor' ||
      to.name === 'WorkDetail' ||
      to.name === 'VraMap' ||
      to.name === 'ElevationMaps' ||
      to.name === 'MapView' ||
      to.name === 'UserInvitations' ||
      to.name === 'BillingMethods' ||
      to.name === 'CasterLog' ||
      to.name === 'UserIcon' ||
      to.name === 'UserPassword'
    )
  }

  function fallbackRoute(to) {
    // 直接復元できない画面は、できるだけ近い一覧画面へ
    if (to.name === 'FieldDetail' || to.name === 'FieldPrint') {
      return { name: 'FieldListAsList' }
    }
    if (to.name === 'TaskDetailError') {
      return { name: 'TaskListAsList' }
    }
    if (to.name === 'RefLineDetail' || to.name === 'RefLineAdd' || to.name === 'RefLineImport') {
      return { name: 'RefLineListAsList' }
    }
    return { name: 'Feed' }
  }
})

// Register Filters
Object.keys(filters).forEach(key => {
  Vue.filter(key, filters[key])
})

new Vue({
  router,
  store,
  vuetify,
  render: h => h(App),
}).$mount('#app')
